import "react";
import accept from "attr-accept";
import { uniqueId, flow, pick, all, negate, isNil } from "lodash/fp";
import { Buffer as BufferPolyfill } from "buffer/index";
import { AttachmentUploadPayload } from "src/queries/attachment";

const Buffer = global.Buffer || BufferPolyfill;

export function createUploadPayloadFromBase64ImageData(
  base64ImageData: string
): AttachmentUploadPayload {
  const { data, contentType } = base64ImageDataToBinary(base64ImageData);
  return {
    data,
    preview: base64ImageData,
    filename: null,
    attachmentType: "photo",
    contentType,
    id: uniqueId(""),
  };
}

// to change Blob to File
const blobToFile = (theBlob: Blob, fileName: string) => {
  const b = theBlob;
  // @ts-ignore
  b.lastModifiedDate = new Date();
  // @ts-ignore
  b.name = fileName;
  return b;
};

export function createUploadPayloadFromVideoBlob(
  blob: Blob,
  mimeType: string
): AttachmentUploadPayload {
  // @ts-ignore
  const [, fileExtension] = /video\/([^;]*)/.exec(mimeType);

  const filename = `RecordRTC-${new Date()
    .toISOString()
    .replace(/:|\./g, "-")}.${fileExtension}`;
  let file;
  // @ts-expect-error msSaveBlob doesn't exist on Navigator
  if (!navigator.msSaveBlob) {
    // detect if not Edge
    // @ts-ignore
    file = new File([blob], document.originalName, { type: mimeType });
  } else {
    file = new Blob([blob], { type: mimeType });
    // @ts-ignore
    file = blobToFile(file, document.originalName);
  }

  return {
    data: file,
    filename,
    attachmentType: "video",
    contentType: `video/${fileExtension}`,
  };
}

export function createUploadPayloadFromMediaUrl(
  mediaUrl: string,
  mimeType: string
) {
  // @ts-ignore
  const [, fileExtension] = /video\/([^;]*)/.exec(mimeType);

  const filename = `RecordRTC-${new Date()
    .toISOString()
    .replace(/:|\./g, "-")}.${fileExtension}`;

  return {
    mediaUrl,
    filename,
    attachmentType: "video",
    contentType: `video/${fileExtension}`,
  };
}

export function createUploadPayloadFromFile(file: File) {
  const basePayload = {
    data: file,
    id: uniqueId(""),
    contentType: file.type,
    size: file.size,
  };

  if (isPhotoFile(file)) {
    return {
      ...basePayload,
      attachmentType: "photo",
      filename: null,
      // @ts-ignore
      preview: file.preview,
    };
  }

  return {
    ...basePayload,
    attachmentType: "file",
    filename: file.name,
    preview: null,
  };
}

export const isValidUploadPayload = flow(
  pick(["id", "attachmentType", "contentType"]),
  all(negate(isNil))
);

function isPhotoFile(file: File) {
  return accept(file, "image/*");
}

interface Dimension {
  width: number;
  height: number;
}
// must be async, unfortunately
type GetImageDimensionsCallback = (dimension: Dimension) => void;
export function getImageDimensions(
  src: string,
  cb: GetImageDimensionsCallback
) {
  const image = new Image();

  image.onload = () => {
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;

    cb({
      width,
      height,
    });
  };

  image.src = src;
}

export async function getImageDimensionsAsync(src: string): Promise<Dimension> {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;
      resolve({ width, height });
    };

    image.onerror = reject;

    image.src = src;
  });
}

const mimeTypePattern = /data:(.*);base64,.*/;

export function base64ImageDataToBinary(base64Data: string) {
  const matchers = base64Data.match(mimeTypePattern);
  const mimeType = matchers && matchers.length > 0 ? matchers[1] : undefined;

  if (mimeType) {
    return {
      contentType: mimeType,
      data: Buffer.from(base64Data.split(",")[1], "base64"),
    };
  } else {
    throw new Error(`Unknown mimeType. Found: ${base64Data.split(";")[0]}`);
  }
}
